import { Card, CardContent } from "~/components/reusable/Card"
import { formatNumber } from "~/utils/formatting"
import { useMemo } from "react"
import { api } from "~/utils/api"
import { type ICollectionDetails, type ICollectionSimple } from "~/hooks/useCollections"
import SuperChart, { CategoryChartSeries } from "~/components/reusable/graphs/SuperChart"
import { first, maxBy, minBy, sumBy, min, max } from "lodash"
import LoadingOverlay from "~/components/reusable/LoadingOverlay"
import { filterNotUndefinedOrNull } from "~/utils/tsUtils"
import { Package } from "@phosphor-icons/react"
import { ChartType } from "~/constants/graphs"
import { useChartSeriesConfig } from "~/constants/colors"
import { categorySeries } from "~/utils/graphUtils"
import { formatName } from "~/utils/namingUtils"
import { groupByMonth } from "~/utils/datetime"
import { MonthCategoryPoint } from "~/utils/datetime"

type PreOrderContentProps = {
  isLoading: boolean
  isLoadingComparison: boolean
  preOrders: { date: Date; value: number }[] | undefined
  series: CategoryChartSeries[]
  startDate: Date
  endDate: Date
}

const PreOrderContent = ({
  isLoading,
  isLoadingComparison,
  preOrders,
  series,
}: PreOrderContentProps) => {
  if (isLoading || isLoadingComparison) {
    return <LoadingOverlay />
  }

  if (!preOrders || preOrders.length === 0) {
    return (
      <div className="text-center label-lg">
        <p>No {formatName("preorder")} data available for the selected collection.</p>
      </div>
    )
  }

  return <SuperChart type={ChartType.CategorySeries} height={220} data={series} />
}

type Props = {
  collections: ICollectionDetails[]
  comparisonCollections?: ICollectionSimple[]
  startDate: Date
  endDate: Date
  comparisonStartDate?: Date
  comparisonEndDate?: Date
  warehouseId?: string
  channel?: string
}

const CollectionPreOrdersGraphCard = ({
  collections,
  comparisonCollections,
  startDate,
  endDate,
  comparisonStartDate,
  comparisonEndDate,
  warehouseId,
  channel,
}: Props) => {
  const chartConfig = useChartSeriesConfig()
  const { data: preOrders, isLoading } = api.preOrder.getPreOrdersForCollectionPeriod.useQuery({
    collectionIds: collections.map((c) => c.id),
    externalCollectionIds: filterNotUndefinedOrNull(collections.map((c) => c.externalId)),
    startDate,
    endDate,
    warehouseId,
    channel,
  })

  const { data: comparisonPreOrders, isLoading: isLoadingComparison } =
    api.preOrder.getPreOrdersForCollectionPeriod.useQuery(
      {
        collectionIds: comparisonCollections?.map((c) => c.id) ?? [],
        externalCollectionIds: filterNotUndefinedOrNull(
          comparisonCollections?.map((c) => c.externalId) ?? [],
        ),
        startDate: comparisonStartDate ?? startDate,
        endDate: comparisonEndDate ?? endDate,
        warehouseId,
        channel,
      },
      { enabled: comparisonCollections !== undefined && comparisonCollections?.length > 0 },
    )

  const preOrdersStartDate = minBy(preOrders, "date")?.date
  const preOrdersEndDate = maxBy(preOrders, "date")?.date

  const preOrdersSum = useMemo(() => sumBy(preOrders, "value"), [preOrders])
  const firstCollection = maxBy(collections, (c) => c.skuCount) ?? first(collections)
  const preOrderName =
    collections.length > 1 ? firstCollection?.collectionGroup?.name : firstCollection?.name
  const preOrdersSeries: CategoryChartSeries = categorySeries(chartConfig.preOrder, {
    name: preOrderName ?? formatName("preorder", true, true),
    type: "column",
    data: groupByMonth(preOrders, { mode: "category" }) as MonthCategoryPoint[],
  })

  const series = [preOrdersSeries]

  if (comparisonCollections !== undefined && comparisonCollections.length > 0) {
    const firstComparison = first(comparisonCollections)
    const comparisonName =
      comparisonCollections?.length > 1 ? firstComparison?.groupName : firstComparison?.name

    const comparisonPreOrdersSeries: CategoryChartSeries = categorySeries(
      chartConfig.preOrderComparison,
      {
        name: comparisonName ?? `Comparison ${formatName("preorder", true)}`,
        type: "column",
        data: groupByMonth(comparisonPreOrders, { mode: "category" }) as MonthCategoryPoint[],
      },
    )
    series.push(comparisonPreOrdersSeries)
  }

  const compStartDate = minBy(comparisonPreOrders, "date")?.date
  const compEndDate = maxBy(comparisonPreOrders, "date")?.date

  const finalStartDate = min([startDate, preOrdersStartDate, compStartDate]) ?? startDate
  const finalEndDate = max([endDate, preOrdersEndDate, compEndDate]) ?? endDate

  return (
    <Card className="w-full">
      <CardContent className="flex flex-col">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <Package className="text-fg-subtle" />
            <div className="label-base text-fg-subtle">{formatName("preorder", true, true)}</div>
          </div>

          <div className="flex items-end gap-1">
            <div className="heading-sm">{preOrdersSum ? formatNumber(preOrdersSum, 0) : "-"}</div>
            <p className="paragraph-base pb-[2px]">Units</p>
          </div>
        </div>
        <div className="flex justify-center items-center w-full h-[220px]">
          <PreOrderContent
            isLoading={isLoading}
            isLoadingComparison={isLoadingComparison}
            preOrders={preOrders}
            series={series}
            startDate={finalStartDate}
            endDate={finalEndDate}
          />
        </div>
      </CardContent>
    </Card>
  )
}

export default CollectionPreOrdersGraphCard
